// src/pages/health/WorkoutPrescriptionVideo.jsx
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoadingGate } from "../../../../hooks/useLoadingGate";
import AuthCard from "../../../../components/auth/AuthCard";
import BottomNav from "../../../../components/common/BottomNav";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const CATEGORIES = [
    { key: "neck", label: "목" },
    { key: "shoulder", label: "어깨" },
    { key: "elbow_wrist", label: "팔 & 팔꿈치" },
    { key: "lumbar", label: "허리" },
    { key: "hip", label: "고관절" },
    { key: "knee", label: "무릎" },
    { key: "ankle", label: "발 & 발목" },


];

const ITEMS_BY_CATEGORY = {
    neck: [
        { key: "straight-neck", title: "일자목 운동" },
        { key: "cervical-disc", title: "경추디스크 운동" },
        { key: "opf", title: "후종인대골화증 운동" },
        { key: "neck-rehab-1", title: "목 재활 운동 1단계" },
        { key: "neck-rehab-2", title: "목 재활 운동 2단계" },
        { key: "neck-rehab-3", title: "목 재활 운동 3단계" },
        { key: "neck-rehab-4", title: "목 재활 운동 4단계" },
    ],
    elbow_wrist: [
        { key: "medial-epicondylitis", title: "내측상과염" },
        { key: "lateral-epicondylitis", title: "외측상과염" },
        { key: "mcl-surgery-rehab", title: "내측측부인대 수술 후 재활" },
        { key: "elbow-fracture-rehab", title: "주관절 골절 후 재활" },
        { key: "elbow-surgery-rehab-1", title: "팔꿈치 수술 후 재활 1단계" },
        { key: "elbow-surgery-rehab-2", title: "팔꿈치 수술 후 재활 2단계" },
        { key: "elbow-surgery-rehab-3", title: "팔꿈치 수술 후 재활 3단계" },
        { key: "elbow-surgery-rehab-4", title: "팔꿈치 수술 후 재활 4단계" },
    ],

    shoulder: [
        { key: "rotator-cuff-tear", title: "회전근개 손상" },
        { key: "impingement-calcific", title: "충돌증후군(석회화건염)" },
        { key: "frozen-shoulder", title: "오십견" },
        { key: "shoulder-instability-sublux", title: "어깨 불안정성(어깨 아탈구)" },
        { key: "functional-shoulder", title: "기능적 어깨" },
        { key: "throwers-shoulder", title: "투구선수 어깨" },
        { key: "postop-shoulder-rehab-1", title: "어깨수술 후 재활 1단계" },
        { key: "postop-shoulder-rehab-2", title: "어깨수술 후 재활 2단계" },
        { key: "postop-shoulder-rehab-3", title: "어깨수술 후 재활 3단계" },
        { key: "postop-shoulder-rehab-4", title: "어깨수술 후 재활 4단계" },
    ],
    lumbar: [
        { key: "herniation", title: "허리디스크 운동" },
        { key: "stenosis", title: "척추관협착증 운동" },
        { key: "facet-syndrome", title: "과절면 증후군 운동" },
        { key: "sciatica", title: "좌골신경통 운동" },
        { key: "lumbar-rehab-1", title: "허리 재활 운동 1단계" },
        { key: "lumbar-rehab-2", title: "허리 재활 운동 2단계" },
        { key: "lumbar-rehab-3", title: "허리 재활 운동 3단계" },
        { key: "lumbar-rehab-4", title: "허리 재활 운동 4단계" },
    ],
    hip: [
        { key: "hip-pelvis-rehab-1", title: "힙+골반재활운동 1단계" },
        { key: "hip-pelvis-rehab-2", title: "힙+골반재활운동 2단계" },
        { key: "hip-pelvis-rehab-3", title: "힙+골반재활운동 3단계" },
        { key: "hip-pelvis-rehab-4", title: "힙+골반재활운동 4단계" },
    ],
    knee: [
        { key: "patellofemoral-pain-syndrome", title: "슬개대퇴통증 증후군" },
        { key: "knee-arthroscopy-rehab", title: "관절내시경 후 재활" },
        { key: "meniscus-surgery-rehab", title: "반월상연골판 수술 후 재활" },
        { key: "acl-surgery-rehab", title: "전방십자인대 수술 후 재활" },
        { key: "knee-arthroplasty-rehab", title: "인공관절 수술 후 재활" },
        { key: "knee-rehab-1", title: "무릎 재활 1단계" },
        { key: "knee-rehab-2", title: "무릎 재활 2단계" },
        { key: "knee-rehab-3", title: "무릎 재활 3단계" },
        { key: "knee-rehab-4", title: "무릎 재활 4단계" },
    ],
    ankle: [
        { key: "achilles-surgery-rehab", title: "아킬레스 수술 후 재활" },
        { key: "ankle-sprain-rehab", title: "발목 인대손상(염좌) 후 재활" },
        { key: "ankle-rehab-1", title: "발목재활 1단계" },
        { key: "ankle-rehab-2", title: "발목재활 2단계" },
        { key: "ankle-rehab-3", title: "발목재활 3단계" },
        { key: "ankle-rehab-4", title: "발목재활 4단계" },
    ],
};

export default function MSKExerciseGuide() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();

    const selectRef = useRef(null);
    const [open, setOpen] = useState(false);
    const initialCat = sp.get("cat") || sessionStorage.getItem("msk_cat") || "neck";
    const [catKey, setCatKey] = useState(initialCat);

    const current = useMemo(
        () => CATEGORIES.find((c) => c.key === catKey) ?? CATEGORIES[0],
        [catKey]
    );

    const handlePick = (key) => {
        setCatKey(key);
        setOpen(false);
        setSp({ cat: key }, { replace: true });
        sessionStorage.setItem("msk_cat", key);
    };

    useEffect(() => {
        if (!sp.get("cat") && sessionStorage.getItem("msk_cat")) {
            setSp({ cat: sessionStorage.getItem("msk_cat") }, { replace: true });
        }
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (!open) return;
            if (selectRef.current && !selectRef.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", onClickOutside);
        return () => window.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    const onButtonKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
        }
        if (e.key === "Escape") setOpen(false);
    };

    const items = ITEMS_BY_CATEGORY[catKey] ?? [];

    const onClickItem = (item) => {
        const qs = new URLSearchParams({
            cat: catKey,
            item: item.key,
            title: item.title,
        }).toString();

        navigate(`/workout/conditionguide/msk/video?${qs}`);
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title={"근골격계 운동 가이드"}
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-3 md:pt-4 pb-24 md:pb-28">
                        {/* 드롭다운 */}
                        <div className="relative" ref={selectRef}>
                            <button
                                type="button"
                                onClick={() => setOpen((v) => !v)}
                                onKeyDown={onButtonKeyDown}
                                className="w-full h-12 md:h-14 px-4 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 flex items-center justify-between"
                                aria-haspopup="listbox"
                                aria-expanded={open}
                                aria-controls="msk-guide-dropdown"
                            >
                                <span className="font-medium">{current.label}</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-700 transition-transform ${open ? "rotate-180" : ""}`}
                                />
                            </button>

                            {open && (
                                <ul
                                    id="msk-guide-dropdown"
                                    role="listbox"
                                    className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white"
                                >
                                    {CATEGORIES.map((c) => {
                                        const selected = catKey === c.key;
                                        return (
                                            <li key={c.key} role="option" aria-selected={selected}>
                                                <button
                                                    className={`w-full text-left h-11 px-4 ${selected ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold" : ""
                                                        }`}
                                                    onClick={() => handlePick(c.key)}
                                                >
                                                    {c.label}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* 리스트 카드 */}
                        <ul className="mt-3 md:mt-4 space-y-3">
                            {items.map((item) => (
                                <li key={item.key}>
                                    <button
                                        onClick={() => onClickItem(item)}
                                        className="w-full px-5 py-3.5 md:py-4 rounded-2xl bg-white border border-gray-200 text-gray-900 text-base md:text-lg font-semibold flex items-center justify-between"
                                    >
                                        <span className="truncate">{item.title}</span>
                                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                                    </button>
                                </li>
                            ))}
                            {items.length === 0 && (
                                <li className="text-center text-gray-500 py-6">준비 중입니다.</li>
                            )}
                        </ul>
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
